import { Body, Controller, Get, Param, Patch, Post, Put, Query, Req, UseGuards } from '@nestjs/common';
import { ApiBearerAuth, ApiSecurity, ApiTags } from '@nestjs/swagger';
import {
  EmailLogStatus,
  SendTestEmailRequest,
  SetTemplateStatusRequest,
  UpsertTemplateRequest,
} from '@cart/shared-types';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { TemplatesService } from './templates.service';

@ApiTags('Admin · Templates')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('admin/templates')
@UseGuards(AuthGuard, AdminAccessGuard)
export class TemplatesController {
  constructor(private readonly templatesService: TemplatesService) {}

  @Get()
  list() {
    return this.templatesService.list();
  }

  // Registered before the ':typeCode' wildcard below — otherwise a request
  // to /admin/templates/email-logs would match :typeCode="email-logs"
  // instead of this route (NestJS/Express resolve same-shape routes in
  // registration order, not by specificity).
  @Get('email-logs')
  listEmailLogs(
    @Query('status') status?: EmailLogStatus,
    @Query('typeCode') typeCode?: string,
    @Query('limit') limit?: string
  ) {
    return this.templatesService.listEmailLogs({
      status,
      typeCode,
      limit: limit ? Number(limit) : undefined,
    });
  }

  @Get(':typeCode')
  getByCode(@Param('typeCode') typeCode: string) {
    return this.templatesService.getByCode(typeCode);
  }

  @Put(':typeCode')
  save(
    @Param('typeCode') typeCode: string,
    @Body() body: UpsertTemplateRequest,
    @Req() req: GuardedRequest
  ) {
    return this.templatesService.save(typeCode, body, req.user.id);
  }

  @Patch(':typeCode/status')
  setStatus(
    @Param('typeCode') typeCode: string,
    @Body() body: SetTemplateStatusRequest,
    @Req() req: GuardedRequest
  ) {
    return this.templatesService.setStatus(typeCode, body.isActive, req.user.id);
  }

  @Get(':typeCode/versions')
  listVersions(@Param('typeCode') typeCode: string) {
    return this.templatesService.listVersions(typeCode);
  }

  @Post(':typeCode/versions/:versionId/restore')
  rollback(
    @Param('typeCode') typeCode: string,
    @Param('versionId') versionId: string,
    @Req() req: GuardedRequest
  ) {
    return this.templatesService.rollback(typeCode, versionId, req.user.id);
  }

  @Post(':typeCode/restore-default')
  restoreDefault(@Param('typeCode') typeCode: string, @Req() req: GuardedRequest) {
    return this.templatesService.restoreDefault(typeCode, req.user.id);
  }

  @Post(':typeCode/send-test')
  sendTest(@Param('typeCode') typeCode: string, @Body() body: SendTestEmailRequest) {
    return this.templatesService.sendTest(typeCode, body);
  }
}
